import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Chip, Button, Tab, Tabs, Avatar } from '@mui/material';
import {
  MailOutline, Security, AdminPanelSettings, DevicesOther,
  FilterAlt, ContactMail, CalendarMonth, Backup,
  SmartToy, Speed, Palette, CloudSync, ArrowForward,
  CheckCircle, Storage, Description, SupportAgent,
  Lock, BarChart, Groups, NotificationsActive,
  Search, Schedule, Rule, Verified, HdrStrong,
  DomainVerification, Shield, Key, ManageAccounts,
  TrendingUp, CloudUpload, Folder, Api,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const stats = [
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '< 200ms', label: 'API Response' },
  { value: 'TLS 1.3', label: 'Encryption' },
  { value: '∞', label: 'Storage Options' },
];

const categories = [
  { id: 'email', label: 'Email & Compose', icon: <MailOutline sx={{ fontSize: 18 }} /> },
  { id: 'security', label: 'Security', icon: <Shield sx={{ fontSize: 18 }} /> },
  { id: 'admin', label: 'Admin & Control', icon: <AdminPanelSettings sx={{ fontSize: 18 }} /> },
  { id: 'collab', label: 'Collaboration', icon: <Groups sx={{ fontSize: 18 }} /> },
  { id: 'ai', label: 'AI & Automation', icon: <SmartToy sx={{ fontSize: 18 }} /> },
  { id: 'infra', label: 'Infrastructure', icon: <HdrStrong sx={{ fontSize: 18 }} /> },
];

const featuresByCategory: Record<string, { icon: React.ReactNode; title: string; desc: string; badge?: string }[]> = {
  email: [
    { icon: <MailOutline />, title: 'Rich HTML Composer', desc: 'Full-featured editor with formatting, inline images, tables, code blocks, and reusable templates. Drafts auto-save every 30 seconds.' },
    { icon: <Search />, title: 'Advanced Search', desc: 'Full-text search across all mailboxes including attachments. Filter by sender, date, size, label, and read status.' },
    { icon: <FilterAlt />, title: 'Smart Filters & Rules', desc: 'Trigger-based rules that auto-sort, label, forward, or archive mail. Supports AND/OR logic and regex patterns.' },
    { icon: <ContactMail />, title: 'Contacts & Groups', desc: 'Centralized address book with VCard support, distribution groups, auto-suggest, and shared org-wide contacts.' },
    { icon: <CalendarMonth />, title: 'Calendar & Tasks', desc: 'Integrated calendar with recurring events, invite management, reminders, task lists, and notes — no external app needed.' },
    { icon: <NotificationsActive />, title: 'Smart Notifications', desc: 'Configurable push/desktop notifications with priority-based filtering. Mute threads, set quiet hours, and snooze emails.' },
  ],
  security: [
    { icon: <Key />, title: 'SPF / DKIM / DMARC', desc: 'Automatic DKIM key generation per domain, SPF enforcement, and DMARC policy with full alignment reporting.' },
    { icon: <Lock />, title: 'Two-Factor Auth', desc: 'TOTP-based 2FA for all users. Admins can enforce org-wide 2FA policy. Backup codes and session management included.' },
    { icon: <Security />, title: 'Anti-Spam & Phishing', desc: 'Rspamd-powered spam filter with custom scoring rules, whitelist/blacklist, quarantine, and real-time threat detection.' },
    { icon: <Verified />, title: 'Encrypted Storage', desc: 'All email data encrypted at rest. TLS 1.3 for all in-transit communication. Zero unauthorized access by design.' },
    { icon: <ManageAccounts />, title: 'Active Session Control', desc: 'View and revoke active login sessions per user across all devices. IP and device fingerprint tracking.' },
    { icon: <Rule />, title: 'IP Access Restrictions', desc: 'Allow-list login by IP range or ASN. Block entire countries or restrict admin panel access to internal networks only.' },
  ],
  admin: [
    { icon: <AdminPanelSettings />, title: 'Full Admin Console', desc: 'Manage users, domains, quotas, aliases, groups, and org settings from a single polished dashboard.' },
    { icon: <DomainVerification />, title: 'Multi-Domain Support', desc: 'Host unlimited domains from one installation. Each domain gets independent DKIM keys, branding, and catch-all rules.' },
    { icon: <BarChart />, title: 'Delivery Reports', desc: 'Live mail traffic analytics, delivery rate charts, bounce tracking, and per-domain SMTP log viewer.' },
    { icon: <Palette />, title: 'White-Label Branding', desc: 'Upload your logo, set brand colors, customize the login page, and deliver a fully branded experience to your users.' },
    { icon: <Backup />, title: 'Backup & Restore', desc: 'Scheduled automated backups with one-click restore. Filter restore by folder, date range, or mailbox.' },
    { icon: <Schedule />, title: 'Email Retention Policies', desc: 'Define auto-delete policies by folder age. Stay compliant with legal hold configurations for specific users or domains.' },
  ],
  collab: [
    { icon: <Storage />, title: 'DriveX File Storage', desc: 'Cloud storage integrated directly into your webmail. Share files internally, mount as a WebDAV network drive, and manage access.' },
    { icon: <Description />, title: 'Docs & Sheets', desc: 'Create and collaborate on documents and spreadsheets powered by OnlyOffice — fully self-hosted, no Google required.' },
    { icon: <SupportAgent />, title: 'Built-in Helpdesk', desc: 'Customer support ticket system with knowledge base, auto-reply, SLA timers, and agent assignment — built right in.' },
    { icon: <Groups />, title: 'User Groups & Aliases', desc: 'Organization-wide groups for mass mailing, shared mailboxes, and catch-all routing. No extra tools needed.' },
    { icon: <CloudSync />, title: 'Email Migration Tool', desc: 'Migrate from Gmail, Outlook, Zoho, or any IMAP provider. Preserves folders, labels, metadata, and attachments.' },
    { icon: <DevicesOther />, title: 'Multi-Device Sync', desc: 'Responsive webmail, IMAP/POP3 for any email client, and mobile-ready UI. Works on every device, everywhere.' },
  ],
  ai: [
    { icon: <SmartToy />, title: 'AI Email Composition', desc: 'One-click AI drafting based on your prompt. Adjust tone, length, and formality. Powered by your choice of LLM backend.' },
    { icon: <Search />, title: 'Smart Summarization', desc: 'Long email threads summarized into key points. Never miss the context of a long conversation again.' },
    { icon: <FilterAlt />, title: 'Intelligent Categorization', desc: 'AI auto-labels emails as Newsletters, Receipts, Notifications, and Priority — keeping your inbox clean automatically.' },
    { icon: <NotificationsActive />, title: 'Smart Reply Suggestions', desc: 'Context-aware reply suggestions that match your writing style. Accept, edit, or ignore with one click.' },
    { icon: <TrendingUp />, title: 'Email Analytics', desc: 'Track open rates, reply rates, and peak hours for your own sending patterns. Optimize communication timing.' },
    { icon: <Schedule />, title: 'Auto-Reply Rules', desc: 'Configurable out-of-office, vacation, and time-based auto-responders with per-sender and per-domain exceptions.' },
  ],
  infra: [
    { icon: <Speed />, title: 'High Performance Stack', desc: 'NestJS API with Redis caching, load-balanced across multiple nodes, handling thousands of requests per second.' },
    { icon: <CloudUpload />, title: 'NFS Shared Storage', desc: 'Shared file storage across API nodes via NFS — ensuring consistent file access for DriveX and Docs across all machines.' },
    { icon: <Api />, title: 'Full REST API', desc: 'Every feature accessible via documented REST API. Build integrations, automation, or your own mobile app on top.' },
    { icon: <Folder />, title: 'Self-Hosted Control', desc: 'Deployed on your own infrastructure (bare metal, VMs, or Proxmox). Your data never leaves your servers.' },
    { icon: <HdrStrong />, title: 'PM2 Process Management', desc: 'Zero-downtime restarts, auto-recovery after crashes, cluster mode, and real-time process monitoring out of the box.' },
    { icon: <Backup />, title: 'Disaster Recovery Ready', desc: 'Multi-VM architecture with independent SMTP, IMAP, API, and DB nodes. Any one node can fail without service disruption.' },
  ],
};

// Hero spotlight features
const spotlights = [
  {
    tag: 'Email Platform', color: '#dc2626',
    icon: <MailOutline sx={{ fontSize: 32, color: '#fff' }} />,
    title: 'Built for Business Email, Not Just Webmail',
    desc: 'CruvzMail handles the full email stack — SMTP sending, IMAP receiving, spam filtering, DKIM signing, and a polished webmail UI — all from one self-hosted installation.',
    points: ['Custom domain email (you@yourcompany.com)', 'Threaded conversations & labels', 'Rich compose with templates & signatures', 'Full-text search across all mailboxes'],
  },
  {
    tag: 'Security First', color: '#1565c0',
    icon: <Shield sx={{ fontSize: 32, color: '#fff' }} />,
    title: 'Enterprise Security Without the Enterprise Price',
    desc: 'From DKIM to 2FA to session control, CruvzMail ships with every security control your IT team expects — configured out of the box, no add-on required.',
    points: ['SPF, DKIM, DMARC automatically configured', 'Rspamd with custom scoring rules', 'TLS 1.3 in transit, encrypted at rest', 'Per-user 2FA enforcement by admin'],
  },
  {
    tag: 'Admin Control', color: '#7c3aed',
    icon: <AdminPanelSettings sx={{ fontSize: 32, color: '#fff' }} />,
    title: 'Complete Admin Control in One Dashboard',
    desc: 'The admin console gives IT managers full visibility and control — from user provisioning to email delivery logs to white-label branding — without touching a terminal.',
    points: ['Multi-domain management', 'Real-time mail traffic & delivery stats', 'Storage quotas & usage per user', 'Role-based admin access'],
  },
];

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('email');

  return (
    <PublicLayout activeNav="Features">

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '140px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1e0a0a 100%)',
      }}>
        <Box sx={{ position: 'absolute', top: -150, right: -150, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Box sx={{ maxWidth: 860, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Feature Overview" sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.04em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.8rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #c4b5fd 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Every Feature Your Team<br />Will Actually Use
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, color: 'rgba(255,255,255,0.65)', maxWidth: 640, mx: 'auto', lineHeight: 1.8, mb: 5 }}>
            CruvzMail is a complete business communication platform — not just email. Explore everything from AI-powered compose to self-hosted file storage and built-in helpdesk.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#dc2626', boxShadow: '0 0 40px rgba(220,38,38,0.4)', '&:hover': { bgcolor: '#b91c1c' } }}>
              Get Started Free
            </Button>
            <Button onClick={() => navigate('/pricing')} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              View Pricing
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0f172a', py: 3, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', md: '1.8rem' }, color: '#fff', lineHeight: 1.1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── SPOTLIGHT SECTIONS ── */}
      {spotlights.map((s, i) => (
        <Box key={s.tag} sx={{ py: { xs: 8, md: 11 }, px: { xs: 2, md: 5 }, bgcolor: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Grid container spacing={6} alignItems="center" direction={i % 2 === 1 ? 'row-reverse' : 'row'}>
              {/* Text */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Chip label={s.tag} size="small" sx={{ mb: 2, bgcolor: `${s.color}12`, color: s.color, fontWeight: 700, fontSize: '0.72rem', border: `1px solid ${s.color}30` }} />
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.1rem' }, lineHeight: 1.25, mb: 2 }}>
                  {s.title}
                </Typography>
                <Typography sx={{ color: '#64748b', lineHeight: 1.8, mb: 3.5, fontSize: '1rem' }}>{s.desc}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {s.points.map((p) => (
                    <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      <CheckCircle sx={{ fontSize: 18, color: s.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.9rem', color: '#334155', fontWeight: 500 }}>{p}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
              {/* Visual card */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{
                  borderRadius: 4, overflow: 'hidden',
                  background: `linear-gradient(135deg, ${s.color}18 0%, ${s.color}06 100%)`,
                  border: `1px solid ${s.color}20`,
                  p: { xs: 3, md: 4 },
                  minHeight: 280,
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2.5,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: 2.5, bgcolor: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px ${s.color}40` }}>
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#0f172a' }}>{s.tag}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>CruvzMail Platform</Typography>
                    </Box>
                  </Box>
                  {s.points.map((p, pi) => (
                    <Box key={pi} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.9)' }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <CheckCircle sx={{ fontSize: 16, color: s.color }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#374151' }}>{p}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ))}

      {/* ── TABBED FEATURE BROWSER ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>All Features</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Explore by Category
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 500, mx: 'auto' }}>
              Over 36 features across 6 categories — all included in every plan.
            </Typography>
          </Box>

          {/* Tab bar */}
          <Box sx={{ mb: 5, display: 'flex', justifyContent: 'center' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                bgcolor: '#f8fafc', borderRadius: '12px', p: 0.5,
                border: '1px solid #e2e8f0',
                '& .MuiTab-root': {
                  textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', borderRadius: '9px',
                  minHeight: 40, px: 2, gap: 0.75, color: '#64748b',
                  '&.Mui-selected': { color: '#dc2626', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
                },
                '& .MuiTabs-indicator': { display: 'none' },
              }}
            >
              {categories.map((c) => (
                <Tab key={c.id} value={c.id} label={c.label} icon={c.icon} iconPosition="start" />
              ))}
            </Tabs>
          </Box>

          {/* Feature grid for active tab */}
          <Grid container spacing={2.5}>
            {(featuresByCategory[activeTab] || []).map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Box sx={{
                  display: 'flex', gap: 2, p: 3, height: '100%', borderRadius: 3,
                  border: '1px solid #f1f5f9',
                  transition: 'all 0.22s',
                  '&:hover': { borderColor: '#fecaca', bgcolor: '#fffbfb', boxShadow: '0 4px 20px rgba(220,38,38,0.06)', transform: 'translateY(-2px)' },
                }}>
                  <Box sx={{ flexShrink: 0, width: 42, height: 42, borderRadius: 2, bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
                    {f.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 0.5 }}>{f.title}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.75 }}>{f.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── COMPARE BLOCK ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #1a0000 100%)' }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#fca5a5', mb: 1.5 }}>Why Switch</Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 2 }}>
            CruvzMail vs. Everyone Else
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', mb: 6, fontSize: '1rem', maxWidth: 560, mx: 'auto' }}>
            Most email providers make you pay extra for storage, helpdesk, or analytics. With CruvzMail, it's all included.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {[
              { label: 'Self-Hosted / Data Ownership', cruvz: true, google: false, zoho: false },
              { label: 'Built-in Helpdesk & Ticketing', cruvz: true, google: false, zoho: false },
              { label: 'Docs & Sheets Collaboration', cruvz: true, google: true, zoho: true },
              { label: 'DriveX Network File Storage', cruvz: true, google: false, zoho: false },
              { label: 'White-Label Custom Branding', cruvz: true, google: false, zoho: true },
              { label: 'AI Email Composition', cruvz: true, google: true, zoho: true },
              { label: 'Full SMTP / IMAP Control', cruvz: true, google: false, zoho: false },
              { label: 'No Per-User Per-Feature Pricing', cruvz: true, google: false, zoho: false },
            ].map((row) => (
              <Grid size={{ xs: 12 }} key={row.label}>
                <Box sx={{
                  display: 'flex', alignItems: 'center',
                  px: 3, py: 1.75,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  gap: 2,
                }}>
                  <Typography sx={{ flex: 1, textAlign: 'left', fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>{row.label}</Typography>
                  {[
                    { name: 'CruvzMail', val: row.cruvz, color: '#dc2626' },
                    { name: 'Google', val: row.google, color: '#64748b' },
                    { name: 'Zoho', val: row.zoho, color: '#64748b' },
                  ].map((col) => (
                    <Box key={col.name} sx={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{col.name}</Typography>
                      {col.val
                        ? <CheckCircle sx={{ fontSize: 20, color: col.name === 'CruvzMail' ? '#4ade80' : '#94a3b8' }} />
                        : <Box sx={{ width: 20, height: 2, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)' }} />
                      }
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: { xs: 2, md: 5 }, textAlign: 'center', background: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 620, mx: 'auto' }}>
          <Chip label="All features included" sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(255,255,255,0.25)' }} />
          <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Ready to Experience the Difference?
          </Typography>
          <Typography sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', mb: 5, lineHeight: 1.75 }}>
            Start free today. No credit card required. Migration assistance included.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#dc2626', bgcolor: '#fff', '&:hover': { bgcolor: '#fef2f2' } }}>
              Start Free Trial
            </Button>
            <Button onClick={() => navigate('/migration')} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Migration Guide
            </Button>
          </Box>
          <Box sx={{ mt: 4, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            {['36+ features included', 'Self-hosted & private', '24/7 support team'].map(t => (
              <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <CheckCircle sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)' }}>{t}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default FeaturesPage;

